import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { AdminSubNav, SUBNAV_INVOICES } from '../../components/admin/AdminSubNav';
import { ExternalPayments } from '../../components/crm/VisitAndBilling';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const money = (cents) => `$${((cents || 0) / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const dollars = (n) => money(Math.round((n || 0) * 100));

const useBilling = () => {
  const { token } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);
  const [invoices, setInvoices] = useState(null);
  const [rec, setRec] = useState(null);
  useEffect(() => {
    axios.get(`${API_URL}/api/admin-invoices`, { headers }).then((r) => setInvoices(r.data)).catch(() => setInvoices([]));
    axios.get(`${API_URL}/api/admin/billing/reconciliation`, { headers }).then((r) => setRec(r.data)).catch(() => setRec(false));
  }, [headers]);
  return { headers, invoices, rec };
};

const Stat = ({ label, value, note, testid }) => (
  <div className="p-5 border-r border-b lg:border-b-0 border-[#e3e0d6] [&:nth-child(2n)]:border-r-0 lg:[&:nth-child(2n)]:border-r lg:last:border-r-0" data-testid={testid}>
    <p className="text-[10px] tracking-[0.12em] uppercase text-[#6f7264]">{label}</p>
    <p className="text-2xl sm:text-[28px] text-[#20211d] mt-3">{value}</p>
    <p className="text-[11px] text-[#77776e] mt-2">{note}</p>
  </div>
);

export function AdminBillingOverviewPage() {
  const { invoices, rec } = useBilling();
  const inv = invoices || [];
  const owed = (i) => i.charge_amount || i.total || 0;
  const open = inv.filter((i) => ['sent', 'overdue'].includes(i.status));
  const overdue = inv.filter((i) => i.status === 'overdue');
  const drafts = inv.filter((i) => i.status === 'draft');
  return (
    <div data-testid="billing-overview-page">
      <AdminSubNav section="Billing" items={SUBNAV_INVOICES} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-xs text-[#6f7264]">Payments made outside the CRM belong here too.</p>
          <div className="flex gap-3">
            <Link to="/admin/billing/payments" className="px-4 py-2.5 rounded-[5px] border border-[#dcded2] bg-white text-xs text-[#20211d]" data-testid="billing-record-external-link">+ Record payment without invoice</Link>
            <Link to="/admin/invoices" className="px-4 py-2.5 rounded-[5px] bg-[#f36a19] text-[#171912] text-xs font-bold" data-testid="billing-create-draft-link">+ Create draft invoice</Link>
          </div>
        </div>
        <section className="grid grid-cols-2 lg:grid-cols-4 rounded-[10px] border border-[#e3e0d6] bg-[#fbfaf7]">
          <Stat label="Recorded payments" value={rec ? money(rec.collected_total_cents) : '–'} note="CRM invoices + outside payments, counted once" testid="billing-stat-collected" />
          <Stat label="Outstanding" value={invoices ? dollars(open.reduce((s, i) => s + owed(i), 0)) : '–'} note={`${open.length} invoice${open.length === 1 ? '' : 's'} with a balance`} testid="billing-stat-outstanding" />
          <Stat label="Overdue" value={invoices ? dollars(overdue.reduce((s, i) => s + owed(i), 0)) : '–'} note={`${overdue.length} need follow-up`} testid="billing-stat-overdue" />
          <Stat label="Draft invoices" value={invoices ? drafts.length : '–'} note="Not sent or collected" testid="billing-stat-drafts" />
        </section>
        <section className="rounded-[10px] bg-[#252a20] text-white p-6 flex flex-wrap items-center justify-between gap-4" data-testid="billing-desk">
          <div>
            <p className="text-[9px] tracking-[0.16em] font-bold text-[#ff985a]">BILLING DESK</p>
            <h2 className="text-xl mt-2">{overdue.length ? `${overdue.length} overdue invoice${overdue.length === 1 ? '' : 's'} to follow up.` : 'No overdue invoices right now.'}</h2>
            <p className="text-xs text-[#c9ccbf] mt-2">See the balance, review the history, and choose the next step. Collected money is not profit.</p>
          </div>
          <Link to="/admin/invoices" className="px-4 py-2.5 rounded-[5px] border border-[#5a5e52] text-xs" data-testid="billing-review-outstanding">Review outstanding ↗</Link>
        </section>
        {rec && rec.external_unlinked_cents > 0 && <p className="text-xs text-[#6f7264] pt-1" data-testid="billing-unlinked-note">{money(rec.external_unlinked_cents)} received outside the CRM isn’t linked to an invoice yet. <Link to="/admin/billing/payments" className="underline">Link it</Link></p>}
      </main>
    </div>
  );
}

export function AdminBillingPaymentsPage() {
  const { headers, invoices } = useBilling();
  return (
    <div data-testid="billing-payments-page">
      <AdminSubNav section="Billing" items={SUBNAV_INVOICES} />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {invoices && <ExternalPayments headers={headers} invoices={invoices} />}
      </main>
    </div>
  );
}
